/**
 * NotificationPanel Component
 *
 * Displays a refined dropdown panel with recent notifications.
 * Features smooth animations, thoughtful empty states, and polished interactions.
 */

import { useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications, useMarkAsRead, useMarkAllAsRead } from '@/services/queries/notifications';
import { Check, Bell, ExternalLink, Inbox, Sparkles } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils/cn';
import { useAuth } from '@/features/auth';

interface NotificationPanelProps {
  onClose: () => void;
}

function getCreatedAtDate(createdAt: string): Date {
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) {
    return new Date();
  }

  // Guard against server/client timezone mismatches that can make "just now"
  // render as "in X hours" (future time). Clamp small future skews to now.
  const now = Date.now();
  const diffMs = parsed.getTime() - now;
  if (diffMs > 60_000 && diffMs < 24 * 60 * 60 * 1000) {
    return new Date(now);
  }

  return parsed;
}

export function NotificationPanel({ onClose }: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: notificationsData, isLoading } = useNotifications({ limit: 20 });
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const [markedAllId, setMarkedAllId] = useState<string | null>(null);

  const notifications = notificationsData?.notifications || [];
  const hasUnread = notifications.some((n) => !n.isRead);
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleNotificationClick = async (notification: typeof notifications[number]) => {
    // Mark as read
    if (!notifications.find((n) => n.id === notification.id)?.isRead) {
      await markAsRead.mutateAsync(notification.id);
    }

    const roles = (user?.roles ?? []).map((role) => role.toLowerCase());
    const quoteId = notification.notification.entityId;

    if (notification.notification.entityType === 'QUOTE' && quoteId) {
      if (roles.includes('admin')) {
        navigate({ to: '/admin/quotes/$id', params: { id: quoteId } });
      } else if (roles.includes('operations')) {
        navigate({ to: '/operations/quotes/$id', params: { id: quoteId } });
      } else {
        navigate({ to: '/client/quotes/$id', params: { id: quoteId } });
      }
    }

    onClose();
  };

  const handleMarkAllAsRead = async () => {
    const id = Math.random().toString(36);
    setMarkedAllId(id);
    await markAllAsRead.mutateAsync();
    setTimeout(() => setMarkedAllId(null), 2000);
  };

  return (
    <div
      ref={panelRef}
      className={cn(
        'absolute right-0 mt-3 w-[420px]',
        'rounded-2xl',
        'bg-white dark:bg-surface-dark',
        'shadow-soft-hover dark:shadow-none',
        'border border-border-light dark:border-border-dark',
        'overflow-hidden',
        'z-50'
      )}
    >
      {/* Header with gradient accent */}
      <div className="relative">
        {/* Subtle gradient accent at top */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary/80 via-primary to-primary/80" />

        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className={cn(
              'flex h-10 w-10 items-center justify-center',
              'rounded-xl bg-gradient-to-br from-primary/10 to-primary/5',
              'text-primary dark:text-primary-light'
            )}>
              <Bell className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Notifications
              </h3>
              {hasUnread && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {unreadCount} unread
                </p>
              )}
            </div>
          </div>

          {hasUnread && (
            <button
              onClick={handleMarkAllAsRead}
              disabled={markAllAsRead.isPending}
              className={cn(
                'group flex items-center gap-1.5',
                'rounded-lg px-3 py-1.5',
                'text-xs font-medium text-primary',
                'bg-primary/5',
                'transition-all duration-200',
                'hover:bg-primary/10',
                'disabled:opacity-50',
                markedAllId && 'bg-green-50 text-green-600'
              )}
            >
              {markedAllId ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Done
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5 opacity-70" />
                  Mark all read
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Notifications list */}
      <div className="max-h-[480px] overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 px-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
              <Bell className="h-5 w-5 animate-pulse text-gray-400" />
            </div>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Loading notifications...
            </p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-5">
            <div className={cn(
              'flex h-16 w-16 items-center justify-center',
              'rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100',
              'dark:from-gray-800 dark:to-gray-900'
            )}>
              <Inbox className="h-7 w-7 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-900 dark:text-white">
              No notifications yet
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center max-w-[280px]">
              When you have notifications, they'll appear here
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {notifications.map((notification, index) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={cn(
                  'group relative cursor-pointer',
                  'transition-all duration-200',
                  'hover:bg-gray-50 dark:hover:bg-gray-800/50',
                  'animate-in fade-in slide-in-from-top-2',
                  !notification.isRead && 'bg-primary/[0.02] dark:bg-primary/[0.05]'
                )}
                style={{ animationDelay: `${index * 30}ms` }}
              >
                {/* Unread indicator bar */}
                {!notification.isRead && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-primary-dark" />
                )}

                <div className="flex items-start gap-4 p-4 pl-5">
                  {/* Status icon */}
                  <div className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                    !notification.isRead
                      ? 'bg-primary/10 text-primary dark:text-primary-light'
                      : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500',
                    'transition-colors duration-200',
                    'group-hover:scale-110',
                    'group-hover:bg-primary/10 group-hover:text-primary'
                  )}>
                    <Sparkles className="h-4 w-4" />
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <p className={cn(
                      'text-sm font-medium leading-snug',
                      !notification.isRead
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400'
                    )}>
                      {notification.notification.title}
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {notification.notification.message}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {formatDistanceToNow(getCreatedAtDate(notification.createdAt), { addSuffix: true })}
                      </p>
                      {!notification.isRead && (
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5',
                          'text-[10px] font-medium',
                          'bg-primary/10 text-primary',
                          'dark:bg-primary/20 dark:text-primary-light'
                        )}>
                          New
                        </span>
                      )}
                    </div>
                  </div>

                  {/* External link indicator */}
                  <div className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center',
                    'rounded-lg',
                    'text-gray-400',
                    'transition-all duration-200',
                    'group-hover:bg-white group-hover:text-primary',
                    'group-hover:shadow-sm',
                    'dark:group-hover:bg-gray-700'
                  )}>
                    <ExternalLink className="h-4 w-4" />
                  </div>
                </div>

                {/* Hover effect overlay */}
                <div className={cn(
                  'absolute inset-0 rounded-lg',
                  'bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0',
                  'opacity-0 transition-opacity duration-200',
                  'group-hover:opacity-100',
                  'pointer-events-none'
                )} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-gray-100 px-5 py-3 dark:border-gray-800">
          <p className="text-xs text-center text-gray-400 dark:text-gray-500">
            Click a notification to view details
          </p>
        </div>
      )}
    </div>
  );
}
